import { Map, ZoomControl } from '@pbe/react-yandex-maps'
import classNames from 'classnames'
import { yandexMap } from 'feature/addresses/redux/addressCreateSelector'
import { FC, useCallback, useEffect, useState } from 'react'
import { Loading } from 'router/Loading'
import { useAppSelector, useActions } from 'store/hooks'
import { useThunkDispatch } from 'store/hooks/useThunkDispatch'

import { GeolocationControlUnit } from './controls/GeolocationControlUnit'
import { RoutePanelControlUnit } from './controls/RoutePanelControlUnit'
import { MarkObject } from './geoObjects/MarkObject'
import { ServiceMarksObjects } from './geoObjects/ServiceMarksObjects'
import { onClickMap } from './redux/actions/onClickMap'
import { ControlsType, IYandexMapProps, MarkType } from './types'
import styles from './YandexMap.module.scss'

export const YandexMap: FC<IYandexMapProps> = ({
  isMapClickable = true,
  onMapClickHandler,
  marks,
  controls,
  className,
  zoomPosition,
}) => {
  const { zoom, center } = useAppSelector(yandexMap)

  const { setIsDirtyAddress } = useActions()
  const thunkDispatch = useThunkDispatch()

  const [isLoading, setIsLoading] = useState(true)

  const settingInst = useCallback(
    (inst: ymaps.Map) => {
      if (!inst) return
      window.map = inst

      if (!isMapClickable) return
      inst.events.add('click', (e: ymaps.IEvent<MouseEvent, {}>) => {
        if (onMapClickHandler) onMapClickHandler(e)
        setIsDirtyAddress(true)
        thunkDispatch(onClickMap({ e }))
      })
    },
    [isMapClickable, onMapClickHandler],
  )

  useEffect(() => {
    return () => {
      if (window.map) window.map.destroy()
      window.map = null
    }
  }, [])

  const zoomPositionObject = zoomPosition ?? {}

  return (
    <>
      {isLoading && <Loading />}
      <Map
        state={{ center, zoom }}
        modules={[
          'control.RoutePanel',
          'geoObject.addon.balloon',
          'geoObject.addon.hint',
        ]}
        options={{ suppressMapOpenBlock: true }}
        instanceRef={settingInst}
        className={classNames(styles.map, className)}
        onLoad={() => setIsLoading(false)}
      >
        {controls?.includes(ControlsType.RoutePanel) && (
          <RoutePanelControlUnit />
        )}
        {controls?.includes(ControlsType.ZoomControl) && (
          <ZoomControl options={zoomPositionObject} />
        )}
        {controls?.includes(ControlsType.GeolocationControlUnit) && (
          <GeolocationControlUnit />
        )}

        {marks?.includes(MarkType.clickMark) && (
          <MarkObject markType={MarkType.clickMark} />
        )}
        {marks?.includes(MarkType.geolocationMark) && (
          <MarkObject markType={MarkType.geolocationMark} />
        )}
        {marks?.includes(MarkType.staticMark) && (
          <MarkObject markType={MarkType.staticMark} />
        )}
        {marks?.includes(MarkType.serviceMarks) && <ServiceMarksObjects />}
      </Map>
    </>
  )
}
